import { States } from '../../constants';
import { OnboardingStep } from './types';

const phoneRegex = `\\(?(\\d{3})\\)?[- ]?|\\s?(\\d{3})[- ]?|\\s?(\\d{4})$`;
const zipCodeRegex = `^[0-9]{5}(?:-[0-9]{4})?$`;

const LISTING_STEP: OnboardingStep = {
  key: 'listing',
  header: 'Venue Listing',
  slim: false,
  getData(data) {
    return data.listing;
  },
  preprocess(data) {
    if (!data.image || !data.image.name) {
      return {
        ...data,
        image: null,
      };
    }

    return data;
  },
  schema: {
    required: ['name', 'address', 'city', 'state', 'zip', 'phone', 'image'],
    properties: {
      name: {
        id: '/properties/name',
        type: 'string',
        title: 'Venue Name',
      },
      address: {
        id: '/properties/address',
        type: 'string',
        title: 'Address',
      },
      city: {
        id: '/properties/city',
        type: 'string',
        title: 'City',
      },
      state: {
        id: '/properties/state',
        type: 'string',
        title: 'State',
        enum: Object.keys(States),
        enumNames: Object.values(States),
      },
      zip: {
        id: '/properties/zip',
        type: 'string',
        title: 'Zip',
        pattern: zipCodeRegex,
      },
      phone: {
        id: '/properties/phone',
        type: 'string',
        title: 'Phone',
        pattern: phoneRegex,
      },
      image: {
        id: '/properties/image',
        type: 'object',
        title: 'Venue Listing Photo',
        // default: 'img',
      },
    },
    type: 'object',
  },
  uiSchema: {
    image: {
      classNames: 'image-upload-field',
      'ui:field': 'imageUploadField',
      'ui:options': {
        aspectRatio: 750 / 280,
        sizeRestrictions: {
          max: 600,
          copy: 'Images must be less than 600 KB. Recommended size: 750px x 280px',
        },
        errorText: 'Venue image required',
      },
    },
    state: {
      classNames: 'noble-custom-select pe-1',
      gridSize: 6,
      'ui:placeholder': 'Select a State',
    },
    zip: {
      classNames: 'ps-1',
      gridSize: 6,
    },
  },
};

const BUSINESS_STEP: OnboardingStep = {
  key: 'business',
  header: 'Business Information',
  slim: false,
  getData(data) {
    return data.business;
  },
  schema: {
    required: ['name', 'address', 'city', 'state', 'zip', 'phone'],
    properties: {
      name: {
        id: '/properties/name',
        type: 'string',
        title: 'Business Name',
      },
      address: {
        id: '/properties/address',
        type: 'string',
        title: 'Address',
      },
      city: {
        id: '/properties/city',
        type: 'string',
        title: 'City',
      },
      state: {
        id: '/properties/state',
        type: 'string',
        title: 'State',
        enum: Object.keys(States),
        enumNames: Object.values(States),
      },
      zip: {
        id: '/properties/zip',
        type: 'string',
        title: 'Zip',
        pattern: zipCodeRegex,
      },
      phone: {
        id: '/properties/phone',
        type: 'string',
        title: 'Phone',
      },
      businessId: {
        id: '/properties/businessId',
        type: 'string',
        title: 'Business ID',
      },
      liquorLicense: {
        id: '/properties/liquorLicense',
        type: 'string',
        title: 'Liquor License',
      },
    },
    type: 'object',
  },
  uiSchema: {
    state: {
      gridSize: 6,
      classNames: 'noble-custom-select pe-1',
    },
    zip: {
      classNames: 'ps-1',
      gridSize: 6,
    },
  },
};

const CONTACT_NO_BIRTHDAY_STEP: OnboardingStep = {
  key: 'contact',
  header: 'Business Contact',
  slim: true,
  getData(data) {
    return data.contact;
  },
  schema: {
    required: ['pointOfContactFirstName', 'pointOfContactLastName', 'email', 'phone'],
    properties: {
      pointOfContactFirstName: {
        id: '/properties/pointOfContactFirstName',
        type: 'string',
        title: 'Point Of Contact First Name',
      },
      pointOfContactLastName: {
        id: '/properties/pointOfContactLastName',
        type: 'string',
        title: 'Point Of Contact Last Name',
      },
      email: {
        id: '/properties/email',
        type: 'string',
        title: 'Email',
      },
      phone: {
        id: '/properties/phone',
        type: 'string',
        title: 'Phone',
        pattern: phoneRegex,
      },
    },
    type: 'object',
  },
  uiSchema: {
    state: {
      classNames: 'noble-custom-select',
    },
    dob: {
      'ui:field': 'datepicker',
    },
  },
};

const CONTACT_WITH_BIRTHDAY_STEP: OnboardingStep = {
  key: 'contact',
  header: 'Business Contact',
  slim: true,
  getData(data) {
    return data.contact;
  },
  schema: {
    required: ['pointOfContactFirstName', 'pointOfContactLastName', 'email', 'phone', 'dob'],
    properties: {
      pointOfContactFirstName: {
        id: '/properties/pointOfContactFirstName',
        type: 'string',
        title: 'Point Of Contact First Name',
      },
      pointOfContactLastName: {
        id: '/properties/pointOfContactLastName',
        type: 'string',
        title: 'Point Of Contact Last Name',
      },
      email: {
        id: '/properties/email',
        type: 'string',
        title: 'Email',
      },
      phone: {
        id: '/properties/phone',
        type: 'string',
        title: 'Phone',
        pattern: phoneRegex,
      },
      dob: {
        id: '/properties/dob',
        type: 'string',
        title: 'Date Of Birth',
        default: new Date(1980, 0, 1).toISOString(),
      },
    },
    type: 'object',
  },
  uiSchema: {
    state: {
      classNames: 'noble-custom-select pe-1',
    },
    dob: {
      'ui:field': 'datepicker',
      classNames: 'ps-1',
    },
  },
};

const PAYMENT_STEP: OnboardingStep = {
  key: 'payment',
  header: 'Payment Details',
  slim: false,
  getData(data) {
    return data.payment;
  },
  schema: {
    required: ['name', 'address', 'city', 'state', 'zip', 'phone', 'routing', 'account', 'taxId'],
    properties: {
      name: {
        id: '/properties/name',
        type: 'string',
        title: 'Legal Business Name',
      },
      address: {
        id: '/properties/address',
        type: 'string',
        title: 'Address',
      },
      city: {
        id: '/properties/city',
        type: 'string',
        title: 'City',
      },
      state: {
        id: '/properties/state',
        type: 'string',
        title: 'State',
        enum: Object.keys(States),
        enumNames: Object.values(States),
      },
      zip: {
        id: '/properties/zip',
        type: 'string',
        title: 'Zip',
        pattern: zipCodeRegex,
      },
      phone: {
        id: '/properties/phone',
        type: 'string',
        title: 'Phone',
        pattern: phoneRegex,
      },
      routing: {
        id: '/properties/routing',
        type: 'string',
        title: 'Routing #',
      },
      account: {
        id: '/properties/account',
        type: 'string',
        title: 'Account #',
      },
      taxId: {
        id: '/properties/taxId',
        type: 'string',
        title: 'Tax ID',
      },
    },
    type: 'object',
  },
  uiSchema: {
    state: {
      classNames: 'noble-custom-select pe-1',
      gridSize: 6,
    },
    zip: {
      classNames: 'ps-1',
      gridSize: 6,
    },
  },
};

export const BRAINTREE_STEPS: OnboardingStep[] = [
  LISTING_STEP,
  BUSINESS_STEP,
  CONTACT_WITH_BIRTHDAY_STEP,
  PAYMENT_STEP,
];

export const FREEDOMPAY_STEPS: OnboardingStep[] = [LISTING_STEP, BUSINESS_STEP, CONTACT_NO_BIRTHDAY_STEP];
